/**
 * Support ticket create / edit form.
 */

import React, { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MdCheck, MdExpandMore, MdSave } from 'react-icons/md';
import { cn } from '../../lib/utils';
import { useToast } from '../../components/shared/Toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface SupportTicket {
  _id?: string;
  ticketNumber?: string;
  subject?: string;
  title?: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  organizationId?: string;
  organizationName?: string;
  assignedTo?: string;
}

interface OrganizationOption {
  _id: string;
  name: string;
}

interface SupportTicketFormProps {
  ticket?: SupportTicket | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const fieldClass =
  'w-full h-9 px-3 text-sm rounded-md border bg-[hsl(var(--background))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/20 focus:border-[hsl(var(--primary))] transition-colors';

const labelClass = 'mb-1 block text-xs font-medium text-[hsl(var(--foreground))]';

function MenuSelect({
  value,
  onChange,
  options,
  placeholder,
  error,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const selected = options.find((option) => option.value === value);

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const width = Math.min(Math.max(rect.width, 240), viewportW - 16);
    let left = rect.left;
    if (left + width > viewportW - 8) left = Math.max(8, viewportW - width - 8);
    const spaceBelow = Math.max(128, viewportH - rect.bottom - 12);
    setMenuStyle({
      position: 'fixed',
      zIndex: 2300,
      top: rect.bottom + 4,
      left,
      width,
      maxHeight: Math.min(260, spaceBelow),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onMove = () => updateMenuPosition();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          fieldClass,
          'flex items-center justify-between gap-2 text-left',
          error ? 'border-red-400' : 'border-[hsl(var(--border))]',
          disabled && 'cursor-not-allowed opacity-60'
        )}
      >
        <span className={cn('truncate', !selected && 'text-[hsl(var(--muted-foreground))]')}>
          {selected?.label || placeholder || 'Select'}
        </span>
        <MdExpandMore className="h-4 w-4 shrink-0 text-[hsl(var(--muted-foreground))]" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="listbox"
              style={menuStyle}
              className="overflow-y-auto rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-1 shadow-xl"
            >
              {options.map((option) => {
                const isSelected = option.value === value;
                return (
                  <button
                    key={option.value || 'empty'}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                      isSelected ? 'bg-[hsl(var(--primary))]/10 font-medium' : 'hover:bg-[hsl(var(--muted))]'
                    )}
                  >
                    <MdCheck className={cn('h-4 w-4 shrink-0', isSelected ? 'text-[hsl(var(--primary))]' : 'text-transparent')} />
                    <span className="truncate text-[hsl(var(--foreground))]">{option.label}</span>
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export function SupportTicketForm({ ticket, onSuccess, onCancel }: SupportTicketFormProps) {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    subject: ticket?.subject || ticket?.title || '',
    description: ticket?.description || '',
    status: ticket?.status || 'open',
    priority: ticket?.priority || 'medium',
    organizationId: ticket?.organizationId || '',
    assignedTo: ticket?.assignedTo || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch organizations for dropdown
  const { data: organizations = [] } = useQuery<OrganizationOption[]>({
    queryKey: ['organizations-list'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/v1/admin/organizations?limit=1000`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (!response.ok) return [];
      const json = await response.json();
      const raw = json.data?.data ?? json.data;
      return Array.isArray(raw) ? raw : [];
    },
  });

  useEffect(() => {
    if (ticket) {
      const rawPriority = (ticket.priority as string) === 'urgent' ? 'critical' : (ticket.priority || 'medium');
      const rawStatus = (ticket.status as string) === 'in-progress' ? 'in_progress' : (ticket.status || 'open');
      setFormData({
        subject: ticket.subject || ticket.title || '',
        description: ticket.description || '',
        status: rawStatus as typeof formData.status,
        priority: rawPriority as typeof formData.priority,
        organizationId: ticket.organizationId || '',
        assignedTo: ticket.assignedTo || '',
      });
    }
  }, [ticket]);

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      try {
        const response = await fetch(`${API_URL}/api/v1/admin/support-tickets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: JSON.stringify({
            title: data.subject,
            description: data.description,
            priority: data.priority,
            organizationId: data.organizationId || undefined,
            assignedTo: data.assignedTo || undefined,
          }),
        });

        if (!response.ok) {
          let errorMessage = 'Failed to create ticket';
          try {
            const error = await response.json();
            errorMessage = error.error || error.message || errorMessage;
          } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }
        return response.json();
      } catch (error: any) {
        // Handle network errors
        if (error.name === 'TypeError' || error.message.includes('fetch')) {
          throw new Error('Network error: Unable to connect to server. Please check your connection.');
        }
        throw error;
      }
    },
    onSuccess: () => {
      showToast('Support ticket created successfully!', 'success');
      onSuccess();
    },
    onError: (error: Error) => {
      setErrors({ submit: error.message });
      showToast(error.message, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      try {
        const response = await fetch(`${API_URL}/api/v1/admin/support-tickets/${ticket?._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: JSON.stringify({
            title: data.subject,
            description: data.description,
            status: data.status,
            priority: data.priority,
            organizationId: data.organizationId || undefined,
            assignedTo: data.assignedTo || undefined,
          }),
        });

        if (!response.ok) {
          let errorMessage = 'Failed to update ticket';
          try {
            const error = await response.json();
            errorMessage = error.error || error.message || errorMessage;
          } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }
        return response.json();
      } catch (error: any) {
        // Handle network errors
        if (error.name === 'TypeError' || error.message.includes('fetch')) {
          throw new Error('Network error: Unable to connect to server. Please check your connection.');
        }
        throw error;
      }
    },
    onSuccess: () => {
      showToast('Support ticket updated successfully!', 'success');
      onSuccess();
    },
    onError: (error: Error) => {
      setErrors({ submit: error.message });
      showToast(error.message, 'error');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validation
    if (!formData.subject.trim()) {
      setErrors({ subject: 'Subject is required' });
      return;
    }

    if (!formData.description.trim()) {
      setErrors({ description: 'Description is required' });
      return;
    }

    if (formData.description.trim().length < 10) {
      setErrors({ description: 'Description must be at least 10 characters' });
      return;
    }

    if (!formData.status) {
      setErrors({ status: 'Status is required' });
      return;
    }

    if (!formData.priority) {
      setErrors({ priority: 'Priority is required' });
      return;
    }

    if (ticket) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const organizationOptions = [
    { value: '', label: 'No organization' },
    ...(organizations ?? []).map((org) => ({ value: org._id, label: org.name })),
  ];

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-2.5">
      <div>
        <label htmlFor="subject" className={labelClass}>
          Subject <span className="text-red-500">*</span>
        </label>
        <input
          id="subject"
          type="text"
          value={formData.subject}
          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          className={cn(fieldClass, errors.subject ? 'border-red-400' : 'border-[hsl(var(--border))]')}
          placeholder="Brief summary of the issue"
        />
        {errors.subject && <p className="mt-1 text-xs text-red-600">{errors.subject}</p>}
      </div>

      <div>
        <label htmlFor="description" className={labelClass}>
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          className={cn(
            'w-full px-3 py-2 text-sm rounded-md border bg-[hsl(var(--background))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/20 focus:border-[hsl(var(--primary))] transition-colors resize-none',
            errors.description ? 'border-red-400' : 'border-[hsl(var(--border))]'
          )}
          placeholder="Describe the issue, including steps to reproduce where relevant"
        />
        {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <div>
          <label className={labelClass}>Organization</label>
          <MenuSelect
            value={formData.organizationId}
            onChange={(value) => setFormData({ ...formData, organizationId: value })}
            options={organizationOptions}
            placeholder="Select organization"
          />
        </div>

        <div>
          <label className={labelClass}>
            Priority <span className="text-red-500">*</span>
          </label>
          <MenuSelect
            value={formData.priority}
            onChange={(value) => setFormData({ ...formData, priority: value as typeof formData.priority })}
            options={[
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
              { value: 'critical', label: 'Urgent' },
            ]}
            error={!!errors.priority}
          />
          {errors.priority && <p className="mt-1 text-xs text-red-600">{errors.priority}</p>}
        </div>

        {ticket ? (
          <div>
            <label className={labelClass}>
              Status <span className="text-red-500">*</span>
            </label>
            <MenuSelect
              value={formData.status}
              onChange={(value) => setFormData({ ...formData, status: value as typeof formData.status })}
              options={[
                { value: 'open', label: 'Open' },
                { value: 'in_progress', label: 'In progress' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'closed', label: 'Closed' },
              ]}
              error={!!errors.status}
            />
            {errors.status && <p className="mt-1 text-xs text-red-600">{errors.status}</p>}
          </div>
        ) : null}

        <div>
          <label htmlFor="assignedTo" className={labelClass}>
            Assigned to
          </label>
          <input
            id="assignedTo"
            type="email"
            value={formData.assignedTo}
            onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
            className={cn(fieldClass, 'border-[hsl(var(--border))]')}
            placeholder="assignee@euroasiann.com"
          />
        </div>
      </div>

      {errors.submit && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
          {errors.submit}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="h-8 px-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="h-8 px-3 rounded-md bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-1.5"
        >
          <MdSave className="h-4 w-4" />
          {isLoading ? (ticket ? 'Saving…' : 'Creating…') : ticket ? 'Save changes' : 'Create ticket'}
        </button>
      </div>
    </form>
  );
}

